/**
 * Dashboard widget scope config and API query params for call-history widgets.
 */
import type { AgentStatusFilter } from './agentStatusFilter';
import { resolveWidgetDateRange, type WidgetDateRange } from './widgetDateRange';

/** Match Worker rollup calendar day (operating-hours timezone). */
export function buildStatusTimeDateParams(config?: WidgetFilterConfig): { date_from: string; date_to: string } {
  const range = (config?.date_range as WidgetDateRange | undefined) ?? 'today';
  if (config?.date_from?.trim() && config?.date_to?.trim()) {
    return { date_from: config.date_from.trim(), date_to: config.date_to.trim() };
  }
  return resolveWidgetDateRange(range);
}

export type StatusTimeView = 'pie' | 'bar';

export type WidgetScope = 'all' | 'campaign' | 'agent';

export type WidgetFilterConfig = {
  icon?: string;
  scope?: WidgetScope;
  campaign_id?: string;
  ipscape_agent_id?: string;
  /** Preferred: today | week (last 7 days incl. today). */
  date_range?: WidgetDateRange;
  date_from?: string;
  date_to?: string;
  filter_type?: 'team' | 'campaign' | 'agent';
  filter_value?: string;
  /** Agent Presence: all | available | unavailable (ipSCAPE status). */
  status_filter?: AgentStatusFilter;
  /** Agent Presence: show campaign names under status (default off for individual agent). */
  show_campaigns?: boolean;
  /** Agent status time: pie chart vs horizontal stacked bar. */
  time_view?: StatusTimeView;
  /** Agent status time: show pause reasons / sub-states when viewing one agent (default on). */
  time_detail?: boolean;
  /** Hourly bar chart: inbound/outbound vs wrap-code stacks. */
  chart_stack?: 'inbound_outbound' | 'wrap_code';
};

export function todayIsoDate(): string {
  return new Date().toISOString().slice(0, 10);
}

function resolvePresenceFilterType(config?: WidgetFilterConfig): 'team' | 'campaign' | 'agent' | null {
  const explicit = config?.filter_type;
  if (explicit === 'team' || explicit === 'campaign' || explicit === 'agent') return explicit;
  if ((config?.ipscape_agent_id ?? '').trim()) return 'agent';
  if ((config?.campaign_id ?? '').trim()) return 'campaign';
  if ((config?.filter_value ?? '').trim() && !config?.campaign_id) return 'team';
  return null;
}

/** Query params for GET /api/agents/status-time (API resolves dates in operating-hours timezone). */
export function buildOutboundAnalyticsParams(config?: WidgetFilterConfig): URLSearchParams {
  const params = new URLSearchParams();
  const range = (config?.date_range as WidgetDateRange | undefined) ?? 'today';
  params.set('range', range === 'week' ? 'week' : 'today');
  return params;
}

export function buildStatusTimeParams(config?: WidgetFilterConfig): URLSearchParams {
  const params = new URLSearchParams();
  const range = (config?.date_range as WidgetDateRange | undefined) ?? 'today';
  params.set('date_range', range);
  if (config?.date_from?.trim() && config?.date_to?.trim()) {
    params.set('date_from', config.date_from.trim());
    params.set('date_to', config.date_to.trim());
  }

  const filterType = resolvePresenceFilterType(config);
  if (filterType === 'agent' && config?.ipscape_agent_id?.trim()) {
    params.set('ipscape_agent_id', config.ipscape_agent_id.trim());
  } else if (filterType === 'campaign' && config?.campaign_id?.trim()) {
    params.set('campaign_id', config.campaign_id.trim());
  } else if (filterType === 'team' && config?.filter_value?.trim()) {
    params.set('team', config.filter_value.trim());
  }
  return params;
}

export function buildCallHistoryParams(config?: WidgetFilterConfig): URLSearchParams {
  const params = new URLSearchParams();
  const scope =
    config?.scope ??
    (config?.campaign_id ? 'campaign' : config?.ipscape_agent_id ? 'agent' : 'all');

  if (scope === 'campaign' && config?.campaign_id) {
    params.set('campaign_id', config.campaign_id);
  }
  if (scope === 'agent' && config?.ipscape_agent_id) {
    params.set('ipscape_agent_id', config.ipscape_agent_id);
  }

  const range = (config?.date_range as WidgetDateRange | undefined) ?? 'today';
  const { date_from, date_to } =
    config?.date_from?.trim() && config?.date_to?.trim()
      ? { date_from: config.date_from.trim(), date_to: config.date_to.trim() }
      : resolveWidgetDateRange(range);
  params.set('date_from', date_from);
  params.set('date_to', date_to);
  params.set('granularity', range === 'week' ? 'day' : 'hour');
  const stack = config?.chart_stack ?? 'inbound_outbound';
  params.set('stack_by', stack === 'wrap_code' ? 'wrap' : 'direction');
  return params;
}

export function colorForChartSeries(index: number): string {
  return WRAP_CHART_COLORS[index % WRAP_CHART_COLORS.length];
}

const INBOUND_COLOR = '#3b82f6';
const OUTBOUND_COLOR = '#22c55e';

export function colorForStackSeries(seriesKey: string, index: number): string {
  if (seriesKey === 'inbound') return INBOUND_COLOR;
  if (seriesKey === 'outbound') return OUTBOUND_COLOR;
  if (seriesKey === 'Other') return '#64748b';
  return colorForChartSeries(index);
}

export function formatDurationSeconds(sec: number | null | undefined): string {
  if (sec == null || sec < 0 || Number.isNaN(sec)) return '—';
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/** Pie / chart segment colours (theme-friendly). */
export const WRAP_CHART_COLORS = [
  '#3b82f6',
  '#22c55e',
  '#f59e0b',
  '#ef4444',
  '#a855f7',
  '#06b6d4',
  '#ec4899',
  '#84cc16',
  '#f97316',
  '#6366f1',
  '#14b8a6',
  '#eab308',
];
